import * as XLSX from "xlsx";
import { fileJsonKeyMapping } from "../models/UserDataFileFormats";
import { Dispatch, SetStateAction } from "react";
import { bulkUploadfileFormat, userTypes } from "../../src/models/UserDataFileFormats";

interface MyItem {
  [key: string]: string;
}

type SetStateType = Dispatch<SetStateAction<boolean>>;
type userTypeKey = keyof typeof userTypes;

const parseYesNo = (value?: unknown): boolean => {
  if (typeof value !== "string") return false;

  const normalized = value.trim().toLowerCase();
  return normalized === "yes";
};

export const parseCSV = (
  data: string,
  setIsStore: SetStateType,
  setIsApplication: SetStateType,
  alertPermissionsConfigured = false,
) => {
  const lines: string[] = data.split("\n");
  const result = [];
  const headerFormat: string[] = lines[0].split(",").map((key) => {
    if (key.trim().toLowerCase() === "application") {
      setIsApplication(true);
    }
    if (key.trim().toLowerCase() === "store") {
      setIsStore(true);
    }
    return key.trim().toLowerCase();
  });
  const headers: string[] = lines[0]
    .split(",")
    .map((key) =>
    key.trim().toLowerCase() === "store"
      ? "dataid"
      : key.trim().toLowerCase() === "application"
      ? "scope"
        : key.trim().toLowerCase(),
  );
  const type = identifyUserType(headers, alertPermissionsConfigured);
  if (type) {
    for (let i = 1; i < lines.length; i++) {
      const obj: { [key: string]: string | string[] | { availability: boolean; inventory: boolean } } = {};
      const currentLine = lines[i].split(",");
      if (currentLine.length === headers.length) {
        for (let j = 0; j < headers.length; j++) {
          const mappedHeader = fileJsonKeyMapping[headers[j]] || headers[j];
          if (mappedHeader === "roles" || mappedHeader === "scopes" || mappedHeader === "dataIds") {
            obj[mappedHeader] = currentLine[j].split(";");
          } else {
            obj[mappedHeader] = currentLine[j];
          }
        }

        // **New columns** - only when alert permissions are configured
        if (alertPermissionsConfigured) {
          obj.alertPermissions = {
            availability: parseYesNo(obj["availability"] as string),
            inventory: parseYesNo(obj["inventory"] as string),
          };
          delete obj["availability"];
          delete obj["inventory"];
        }

        result.push(obj);
      }
    }
  }
  return { result, headerFormat, userType: type };
};

export const parseXLSX = (
  data: ArrayBuffer,
  setIsStore: SetStateType,
  setIsApplication: SetStateType,
  alertPermissionsConfigured = false,
) => {
  const arrayBuffer = data;
  const array = new Uint8Array(arrayBuffer);
  const binaryString = Array.from(array, (byte) => String.fromCharCode(byte)).join("");
  const workbook = XLSX.read(binaryString, { type: "binary" });
  const first_sheet_name = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[first_sheet_name];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const XLSXParseData = XLSX.utils.sheet_to_json(worksheet, { raw: true }) as MyItem[];
  const headerFormat: string[] = Object.keys(XLSXParseData[0] || {}).map((key) => {
    if (key.trim().toLowerCase() === "application") {
      setIsApplication(true);
    }
    if (key.trim().toLowerCase() === "store") {
      setIsStore(true);
    }
    return key.trim().toLowerCase();
  });
  const headers = Object.keys(XLSXParseData[0] || {}).map((key) =>
    key.trim().toLowerCase() === "store"
      ? "dataid"
      : key.trim().toLowerCase() === "application"
      ? "scope"
      : key.trim().toLowerCase(),
  );

  const processedData = XLSXParseData.map((item: MyItem) => {
    const processedItem: { [key: string]: string | string[] | { availability: boolean; inventory: boolean } } = {};
    const keys: string[] = Object.keys(item);
    for (let i = 0; i < keys.length; i++) {
      const key: string = keys[i];
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        const mappedKey = fileJsonKeyMapping[key.toLowerCase()] || key.toLowerCase();
        if (mappedKey === "roles" || mappedKey === "scopes" || mappedKey === "dataIds") {
          processedItem[mappedKey] = typeof item[key] === "string" ? item[key].split(";") : [`${item[key]}`];
        } else {
          processedItem[mappedKey] = item[key];
        }
      }
    }

    // **New columns** - only when alert permissions are configured
    if (alertPermissionsConfigured) {
      processedItem.alertPermissions = {
        availability: parseYesNo(processedItem["availability"] as string),
        inventory: parseYesNo(processedItem["inventory"] as string),
      };
      delete processedItem["availability"];
      delete processedItem["inventory"];
    }

    return processedItem;
  });
  const type = identifyUserType(headers, alertPermissionsConfigured);
  return type
    ? { processedData, headerFormat, userType: type }
    : { processedData: [], headerFormat: [], userType: type };
};

const identifyUserType = (headers: string[], alertPermissionsConfigured = false) => {
  let userType = "";
  for (const [key, value] of Object.entries(bulkUploadfileFormat)) {
    const expected = alertPermissionsConfigured ? value : value.filter((v) => v !== "availability" && v !== "inventory");
    if (headers.length === expected.length && headers.every((item) => expected.includes(item))) {
      userType = userTypes[key as userTypeKey];
    }
  }
  return userType;
};
